// How many channels a device may play at once — browser mirror of
// server/device-models.js.
//
//                  │ output_type='ip'   │ output_type='hdmi'
//   ───────────────┼────────────────────┼────────────────────
//   RK3568         │ 6                  │ 1
//   RK3288         │ 1                  │ 1
//   unknown model  │ 1                  │ 1
//
// HDMI is always 1: the board has a single CRTC and a single stereo ALSA card.
// IP out repackages to UDP instead of decoding, which is what makes six
// concurrent channels possible on an RK3568.
//
// Both spellings of each model must resolve: the admin UI offers
// 'eBox-3288' / 'eBox-3568', while devices report 'RK3288W' / 'RK3568' at
// registration. Unknown models fall back to 1 — never 6.
//
// The server is authoritative. GET /devices and GET /devices/:id return
// `max_channels`, and GET /devices/channel-limits returns `max_slots`; prefer
// those when present and use this table only as the fallback for rows fetched
// from endpoints that don't carry it (e.g. affiliate dashboards).
const MODEL_FAMILY_BY_ALIAS = {
  'ebox-3568': 'RK3568',
  'rk3568':    'RK3568',
  'ebox-3288': 'RK3288',
  'rk3288':    'RK3288',
  'rk3288w':   'RK3288',
};

const MAX_CHANNELS_BY_FAMILY = { RK3568: 6, RK3288: 1 };

const MAX_CHANNELS_BY_MODEL = Object.fromEntries(
  Object.entries(MODEL_FAMILY_BY_ALIAS)
    .map(([alias, family]) => [alias, MAX_CHANNELS_BY_FAMILY[family]])
);

/**
 * Hardware families an OTA update may be scoped to. The server validates this
 * again on upload (server/device-models.js is authoritative); this list only
 * populates the picker.
 */
export const MODEL_FAMILIES = Object.keys(MAX_CHANNELS_BY_FAMILY);

/** Canonical family for a model string ('eBox-3568' → 'RK3568'), or null. */
export function modelFamily(model) {
  const key = String(model || '').trim().toLowerCase();
  return MODEL_FAMILY_BY_ALIAS[key] || null;
}

export const ABSOLUTE_MAX_CHANNELS = 6;
export const HDMI_MAX_CHANNELS = 1;
export const DEFAULT_OUTPUT_TYPE = 'ip';

// Labels for the output-type picker.
export const OUTPUT_TYPE_OPTIONS = [
  { value: 'ip',   label: 'IP out (UDP to LAN)', hint: 'Relays each channel to the LAN. Up to 6 on an RK3568.' },
  { value: 'hdmi', label: 'HDMI out (screen)',   hint: 'Decodes one channel to the screen. Always 1 channel.' },
];

/** Coerce any value to a valid output type ('ip' | 'hdmi'). */
export function normalizeOutputType(outputType) {
  const key = String(outputType || '').trim().toLowerCase();
  return key === 'hdmi' ? 'hdmi' : DEFAULT_OUTPUT_TYPE;
}

export function outputTypeLabel(outputType) {
  return normalizeOutputType(outputType) === 'hdmi' ? 'HDMI out' : 'IP out';
}

/** Max concurrent channels the MODEL could carry, ignoring output type. */
export function maxChannelsForModel(model) {
  const key = String(model || '').trim().toLowerCase();
  return Math.min(MAX_CHANNELS_BY_MODEL[key] || 1, ABSOLUTE_MAX_CHANNELS);
}

/** The real cap: model AND output type together. */
export function maxChannelsFor(model, outputType) {
  if (normalizeOutputType(outputType) === 'hdmi') return HDMI_MAX_CHANNELS;
  return maxChannelsForModel(model);
}

/**
 * Capacity for a device row, preferring whatever the server sent.
 * Accepts rows shaped like devices (`max_channels`, `device_model`) or like
 * channel-limits entries (`max_slots`, `device_model`).
 */
export function maxChannelsForDevice(device) {
  if (!device) return 1;
  const fromServer = device.max_channels ?? device.max_slots;
  if (Number.isFinite(Number(fromServer)) && Number(fromServer) > 0) {
    return Math.min(Number(fromServer), ABSOLUTE_MAX_CHANNELS);
  }
  // EditAffiliate/AffiliateManagement carry these as `model`/`outputType` on
  // their local device shapes; DeviceList carries them as `device_model`/
  // `output_type`.
  return maxChannelsFor(
    device.device_model ?? device.model,
    device.output_type ?? device.outputType,
  );
}

/** True when this device may hold more than one channel at a time. */
export function isMultiChannelDevice(device) {
  return maxChannelsForDevice(device) > 1;
}
